import { graphqlClient } from '@/lib/graphql-client';
import {
  orgCalendarDate,
  dateOnlyRangeClause,
  parseIsoDate,
  weekdayNameOf,
} from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { DatedHrTerm, hrTermOn, workDaysOn } from '@/lib/utils/work-days/work-days';
import { buildGetHolidaysOnDateQuery, buildGetHrWorkDaysQuery } from '../graphql/queries';

/** `isEmployeeWeekendDate` takes a list of WORK days (weekend = "not in this list"), so the
 * fallback used when the employee has no current-year HR profile (or it has no WorkDays set yet)
 * must itself be a work-days list — Monday-Friday, i.e. the conventional Saturday/Sunday weekend
 * — not the weekend days themselves. The legacy backend instead treated a missing profile as "not
 * a weekend" and silently blocked TOIL entirely for anyone without one, which would make the
 * feature unusable for real gaps in HR data instead of just for genuine regular workdays. */
const DEFAULT_WORK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/** One working day of an HR term, in decimal hours (8.5 = 8 h 30 min). */
interface HrWorkDay {
  WorkDay: string;
  WorkingHours: number;
  EffectiveFrom?: string | null;
}

/** The working days of the employee's HR term covering `day` (its embedded `WorkDays` list, every
 * setting); empty when there is no term or it has no work days. */
const getHrWorkDaysOn = async (userId: string, day: string): Promise<HrWorkDay[]> => {
  if (!userId) return [];
  const result = (await graphqlClient.query({
    query: buildGetHrWorkDaysQuery(userId),
  })) as { HrProfiles: { items: (DatedHrTerm & { WorkDays?: HrWorkDay[] | null })[] } };
  return (hrTermOn(result.HrProfiles.items, day)?.WorkDays ?? []).filter(
    (row) => typeof row?.WorkDay === 'string'
  );
};

/** The employee's working days on `day` ("YYYY-MM-DD"): the work-day setting in force that day of the HR
 * term covering it, or the Monday-Friday default (i.e. a conventional Saturday/Sunday weekend) when none
 * is on file. */
export const getEmployeeWorkDays = async (userId: string, day: string): Promise<string[]> => {
  const workDays = workDaysOn(await getHrWorkDaysOn(userId, day), day).map((row) => row.WorkDay);
  return workDays.length > 0 ? workDays : DEFAULT_WORK_DAYS;
};

/** A date is the employee's "weekend" when its weekday isn't one of their configured work days. */
export const isEmployeeWeekendDate = (dateIso: string, workDays: string[]): boolean => {
  const weekday = weekdayNameOf(dateIso);
  return weekday ? !workDays.includes(weekday) : false;
};

/** Whether the organization has a holiday on `dateIso` (`YYYY-MM-DD`; `Holiday.StartDate` is date only). */
export const isPublicHolidayDate = async (dateIso: string): Promise<boolean> => {
  if (!parseIsoDate(dateIso)) return false;

  const result = (await graphqlClient.query({
    query: buildGetHolidaysOnDateQuery(dateOnlyRangeClause(dateIso, dateIso) ?? {}),
  })) as { Holidays: { totalCount: number } };
  return result.Holidays.totalCount > 0;
};

/**
 * The only check made before a TOIL request is sent to the TOIL_REQUEST workflow, done in the browser
 * without a request: the date can't be before the employee's today in the organization's time zone
 * (or unreadable). Everything else — a duplicate
 * request for the same day, whether the date is one of the employee's work days, and the resulting
 * `ToilCategory` — is the workflow's job.
 */
export const isPastToilDate = (
  dateIso: string,
  now: Date = new Date(),
  timeZone: string = getOrganizationTimeZone()
): boolean => !parseIsoDate(dateIso) || dateIso < orgCalendarDate(now.toISOString(), timeZone);
